import { useMemo, useState } from 'react';
import { Button, Tag, IconButton } from '@teo/components';
import { RotateLeft, RotateRight, Expand } from '@teo/components/icons';

function processHtml(htmlString) {
  if (!htmlString) return { htmlWithPlaceholders: '', images: [] };

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const imgs = Array.from(doc.querySelectorAll('img'));

  const images = [];
  imgs.forEach((img, idx) => {
    const index = idx + 1;
    const src = img.getAttribute('src') || '';
    const alt = img.getAttribute('alt') || '';
    const title = `image${index}`;

    const placeholderHTML = `
        <span class="flex flex-row items-center gap-1" data-image-index="${index}">{
          <span class="w-4 h-4" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M15.75 6a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5ZM15 8.25a.75.75 0 1 1 1.5 0 .75.75 0 0 1-1.5 0Z" fill="currentColor"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.492 21a.46.46 0 0 0 .016 0H19.5a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h5.992ZM3 6a1.5 1.5 0 0 1 1.5-1.5h15A1.5 1.5 0 0 1 21 6v9.648l-2.218-1.85a2.25 2.25 0 0 0-3.031.14L14.63 15.06l-4.1-4.092a2.252 2.252 0 0 0-3.084-.091L3 14.83V6Zm10.57 10.12-4.1-4.091a.75.75 0 0 0-1.028-.03L3 16.836V18a1.5 1.5 0 0 0 1.5 1.5h5.69l3.38-3.38ZM21 17.601V18a1.5 1.5 0 0 1-1.5 1.5h-7.19l4.502-4.502a.75.75 0 0 1 1.01-.047L21 17.601Z" fill="currentColor"/>
            </svg></span>
            <span class="">${title}</span>}
        </span>
      `;

    images.push({ title, src, alt });
    const span = doc.createElement('span');
    span.innerHTML = placeholderHTML.trim();
    img.replaceWith(span.firstChild);
  });

  const htmlWithPlaceholders = doc.body.innerHTML;
  const clone = doc.body.cloneNode(true);
  clone.querySelectorAll('[data-image-index]').forEach((el) => el.remove());

  let residualText = (clone.textContent || '').replace(/\u00A0/g, ' ').trim();

  residualText = residualText.replace(/\{\s*image\d+\s*\}/gi, '');
  residualText = residualText.replace(/image\d+/gi, '');

  const hasRealText = residualText.replace(/\s+/g, '').length > 0;
  return { htmlWithPlaceholders, images, hasRealText };
}

export default function RichTextWithExtractedImages({ html }) {
  const [magnifyImg, setMagnifyImg] = useState(false);
  const [rotateImg, setRotateImg] = useState(0);
  const [fullscreenImg, setFullscreenImg] = useState(false);
  const normalizedImg = ((rotateImg % 360) + 360) % 360;
  const [activeMagnifyImg, setActiveMagnifyImg] = useState(null);
  const isVerticalImg = normalizedImg === 90 || normalizedImg === 270;
  const { htmlWithPlaceholders, images, hasRealText } = useMemo(
    () => processHtml(html),
    [html]
  );

  const ImageOverlay = ({ img }) => (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#000000cc]"
      onClick={() => setMagnifyImg(false)}
    >
      <div
        className="pointer-events-none flex h-full w-full items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          alt={img.title}
          src={img.src}
          className="pointer-events-auto object-contain transition-transform duration-300"
          style={{
            transform: `rotate(${rotateImg}deg)`,
            maxWidth: fullscreenImg
              ? isVerticalImg
                ? '100vh'
                : '100vw'
              : '100vmin',
            maxHeight: fullscreenImg
              ? isVerticalImg
                ? '100vw'
                : '100vh'
              : '100vmin',
          }}
        />
      </div>

      <OverlayButtons
        rotate={rotateImg}
        setRotate={setRotateImg}
        onToggleFull={() => setFullscreenImg((f) => !f)}
        onClose={() => setMagnifyImg(false)}
      />
    </div>
  );

  const OverlayButtons = ({ setRotate, onToggleFull, onClose }) => (
    <div
      className="fixed top-4 right-4 z-[9999] flex gap-2 rounded bg-[#00000088] p-2"
      onClick={(ev) => ev.stopPropagation()}
    >
      <IconButton
        Icon={Expand}
        className="pointer-events-auto fill-white"
        variant="ghost"
        size="md"
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          onToggleFull();
        }}
      />
      <IconButton
        Icon={RotateLeft}
        className="pointer-events-auto fill-white"
        variant="ghost"
        size="md"
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          setRotate((r) => r - 90);
        }}
      />
      <IconButton
        Icon={RotateRight}
        className="pointer-events-auto fill-white"
        variant="ghost"
        size="md"
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          setRotate((r) => r + 90);
        }}
      />
      <IconButton
        Icon={() => <div className="text-2xl">X</div>}
        className="pointer-events-auto text-white"
        variant="ghost"
        size="md"
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          onClose();
        }}
      />
    </div>
  );

  return (
    <div className="mt-2 flex w-full flex-col">
      {hasRealText && (
        <div
          className="text-sm font-normal text-grey-05"
          dangerouslySetInnerHTML={{
            __html: htmlWithPlaceholders,
          }}
        ></div>
      )}

      <div className="">
        {images.length > 0 && (
          <div className="flex flex-col gap-2">
            {images.map((img, i) => (
              <div
                key={i + '_' + img.title}
                className="relative flex flex-row items-center rounded border border-grey-02 bg-white p-3 md:max-w-[50%] md:flex-[1_0_49%]"
              >
                <Button
                  variant="ghost"
                  className="mr-3 !p-0"
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setRotateImg(0);
                    setMagnifyImg(true);
                    setActiveMagnifyImg(img.title);
                  }}
                >
                  <img
                    alt={img.title}
                    src={img.src}
                    className="h-16 w-16 rounded border border-tint-indigo object-cover object-center"
                  />
                </Button>
                {magnifyImg && activeMagnifyImg === img.title && (
                  <ImageOverlay img={img} />
                )}

                <div>
                  <p className="w-[150px] max-w-[150px] overflow-hidden truncate pb-1 text-base font-semibold">
                    {img.title}
                  </p>
                  <Tag
                    title={'Image'}
                    size="md"
                    variant="document"
                    documentType={'image'}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
